import React, { Component, ReactNode } from 'react';
import { ColumnProps } from 'primereact/column';
import moment from 'moment';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import CustomEmptyComponent from 'components/custom/CustomEmptyComponent';
import CustomTable, { CustomColumnProps } from 'components/custom/CustomTable';
import CustomPaginator, { CustomPaginatorProps } from 'components/custom/CustomPaginator';
import { CaseActionHistory, CaseActionHistoryItem, CaseEventLog, CaseSettings } from '../../../domain/case/CaseDomains';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { dispatch, RootState } from '../../../redux/store';
import {
  TransactionData,
  TransactionJsonModel,
  TransactionReason,
  TransactionResult,
  TransactionResultReason,
  TransactionVariableJsonModel,
} from '../../../domain/stream/transaction/TransactionDomains';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import DataType from '../../../constant/DataType';
import { DataStructure } from '../../../domain/structure/DataStructureDomains';
import {
  CustomStructureProperties,
  DateVariableProperties,
  ObjectVariableProperties,
} from '../../../domain/datatype/VariablePropertiesDomains';
import DateFormatUtil from '../../../util/DateFormatUtil';
import { DataTableColReorderParams, DataTableRowClickEventParams, DataTableSortMeta } from 'primereact/datatable';
import NavigationUtil from '../../../util/NavigationUtil';
import { Tooltip } from 'primereact/tooltip';
import { ComponentState, setSelectedItems } from '../../../redux/actions/ComponentActions';
import Icon from '../../../icons/Icon';
import TransactionFailureModalComponent from './widget/TransactionFailureModalComponent';
import CustomTableContentWrapper from '../../custom/CustomTableContentWrapper';
import { Dialog } from 'primereact/dialog';
import { Tree } from 'primereact/tree';
import { generateUUID } from '../../../util/Generator';
import TreeNode from 'primereact/treenode';
import { User, UserSystemAttributes } from '../../../domain/rbac/RbacDomains';
import TransactionResultModalComponent from './widget/TransactionResultModalComponent';
import ActionHistoryModalComponent from './widget/ActionHistoryModalComponent';
import { CaseState } from '../../../redux/reducers/CaseReducer';
import { changeCaseColumns, changeListDataLoading } from '../../../redux/actions/CaseActions';
import ComponentName from '../../../constant/ComponentName';
import { setAttribute } from '../../../redux/actions/UserActions';
import ValueFactory from '../../../util/ValueFactory';
import CustomSkeleton from '../../custom/CustomSkeleton';
import {
  CustomFilter as FilterType,
  CustomFilterCategorical,
  CustomFilterDateSearch,
  CustomFilterFields,
  CustomFilterLiteralSearch,
} from '../../../domain/ui/FilterDomains';
import { PageState } from '../../../redux/reducers/PageReducer';
import { changePageFilter } from 'redux/actions/PageActions';
import FilterUtil from '../../../util/FilterUtil';
import LanguageUtil from '../../../util/LanguageUtil';
import UrlUtil from '../../../util/UrlUtil';
import CaseService from '../../../service/CaseService';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;
import Order = query.Order;
import Computation = subscribe.Computation;
import Filter = query.Filter;
import ValueUtil from 'util/ValueUtil';
import CaseEventTextUtil from '../../../util/CaseEventTextUtil';
import or = query.or;
import and = query.and;

const ACTION_EVENT_TYPES: CaseEventLog['type'][] = ['GO', 'HOLD', 'CLOSE', 'ACTION', 'LIST_ADD', 'LIST_REMOVE'];

class CaseTransactionsActivityComponent extends Component<Props, State> {
  public state: State = new State();

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly caseService: CaseService = ServiceContainer.getInstance().get(ServiceNames.CASE);
  private actionHistoryRequest = 0;
  private actionHistoryVisible = false;
  private actionLogIds: string | undefined;
  private actionLogSubscription: subscribe.Subscription | undefined;
  private actionLogComputation: Computation | undefined;
  private userSystemAttributeSubscription: subscribe.Subscription | undefined;
  private dataStructureSubscription: subscribe.Subscription | undefined;
  private computation: Computation | undefined;

  public render(): ReactNode {
    const { currentPage, rows, transactionFailureVisible, transactionFailures, structureVisible, structureData, transactionResultVisible, transactionResult } =
      this.state;
    const {
      transactions,
      paginationFixed,
      total,
      multiple,
      loading,
      component: { selectedComponentIds },
      onSortChange,
    } = this.props;
    const columns = this.getColumns();
    const isEmpty = transactions.length == 0 && columns.length > 0 && !loading;
    const isLoading = loading || columns.length == 0;

    return (
      <div className='w-full h-full flex flex-col overflow-y-scroll max-h-[calc(100vh-400px)] 2xl:max-h-[calc(100vh-400px)] xl:max-h-[calc(100vh-380px)] '>
        {isEmpty ? (
          <div className={'h-[100vh] w-full flex items-center justify-center'}>
            <CustomEmptyComponent
              label={LanguageUtil.get({
                id: 'components.emptyComponent.anyTransaction',
                defaultMessage: 'You currently do not have any transactions',
              })}
            />
          </div>
        ) : isLoading ? (
          <CustomSkeleton />
        ) : (
          <div className='flex flex-col h-full'>
            <div className='flex justify-between bg-white items-center w-full flex-1 min-h-0'>
              <CustomTable
                columns={columns}
                key={this.props.filterKey}
                rowClassName={this.onRowClassName}
                onRowClick={this.onRowClick}
                onSortChange={onSortChange}
                value={transactions}
                selectionMode={multiple ? 'checkbox' : undefined}
                onSelectionChange={this.onSelectionChange}
                selection={selectedComponentIds}
                reorderableColumns
                multiSortMeta={this.getOrder()}
                onColReorder={this.onColReorder}
              />
            </div>
            <CustomPaginator
              currentPage={currentPage}
              currentRecords={transactions.length}
              key='case-transaction-activity'
              rows={rows}
              totalRecords={total > 10_000 ? 10_000 : total}
              loading={loading}
              fixed={paginationFixed}
              rowsPerPageOptions={[5, 10, 20, 50, 100]}
              onPageChange={this.onPageChange}
            />
          </div>
        )}
        <TransactionFailureModalComponent failures={transactionFailures} visible={transactionFailureVisible} onClose={this.closeFailures} />
        {transactionResult && <TransactionResultModalComponent result={transactionResult} visible={transactionResultVisible} onClose={this.closeResult} />}
        <ActionHistoryModalComponent
          actions={this.state.selectedActionHistory}
          visible={this.state.actionHistoryVisible}
          onClose={this.closeActionHistory}
        />
        <Dialog
          className={'h-1/2 w-1/3'}
          headerClassName={'!pb-0'}
          closable={false}
          header={<span className={'text-[15px] text-normal'}>Structure Items</span>}
          dismissableMask
          closeOnEscape
          visible={structureVisible}
          onHide={this.structureModalClose}
        >
          <Tree value={structureData} className={'border-none h-full w-full text-[13px] p-0 pt-2'} />
        </Dialog>
      </div>
    );
  }

  public async componentDidMount() {
    const { currentPage, rows, changeListDataLoading } = this.props;
    changeListDataLoading(true);
    if (currentPage != undefined) {
      this.setState({ currentPage });
    }
    if (rows != undefined) {
      this.setState({ rows });
    }
    this.startTracker();
    this.refreshActionHistory();
  }

  private isActionHistoryVisible = (): boolean => {
    return this.getColumns().some((c) => (c.field == 'firstActionAt' || c.field == 'actionHistory') && !c.hidden);
  };

  private refreshActionHistory = () => {
    const ids = this.props.transactions.map((t) => t.id);
    this.actionHistoryVisible = this.isActionHistoryVisible();
    if (ids.length == 0 || !this.actionHistoryVisible) {
      this.stopActionLogTracker();
      return;
    }
    const key = ids.join(',');
    if (key != this.actionLogIds) {
      this.stopActionLogTracker();
      this.actionLogIds = key;
      const logQuery = QueryBuilder.select()
        .where(and(or(...ids.map((id) => eq('transactionId', id))), or(...ACTION_EVENT_TYPES.map((type) => eq('type', type)))))
        .limit(1)
        .orderBy({ field: 'timestamp', operator: 'DESC' })
        .build();
      this.actionLogSubscription = subscribe.Formica.subscribe(`case_event_log_${NavigationUtil.getTenant()}_*`, logQuery, undefined, 'elasticsearch');
      this.actionLogComputation = subscribe.Tracker.autorun(() => {
        if (this.actionLogSubscription?.ready()) {
          this.loadActionHistory();
        }
      }, [this.actionLogSubscription]);
    }
    this.loadActionHistory();
  };

  private stopActionLogTracker = () => {
    this.actionLogComputation?.stop();
    this.actionLogSubscription?.stop();
    this.actionLogComputation = undefined;
    this.actionLogSubscription = undefined;
    this.actionLogIds = undefined;
  };

  private loadActionHistory = async () => {
    const { transactions } = this.props;
    const request = ++this.actionHistoryRequest;
    try {
      const actionHistory = await this.caseService.getActionHistory(transactions.map((t) => t.id));
      if (request == this.actionHistoryRequest) {
        this.setState({ actionHistory: actionHistory ?? {} });
      }
    } catch (e) {
      console.error(e);
    }
  };

  private startTracker = () => {
    const { user } = this.props;
    const attrQuery = QueryBuilder.select().from(CollectionNames.USER_SYSTEM_ATTRIBUTES).where(eq('username', user.username)).build();
    const structQuery = QueryBuilder.select().from(CollectionNames.DATA_STRUCTURES).build();
    this.userSystemAttributeSubscription = subscribe.Formica.subscribe(CollectionNames.USER_SYSTEM_ATTRIBUTES, attrQuery);
    this.dataStructureSubscription = subscribe.Formica.subscribe(CollectionNames.DATA_STRUCTURES, structQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.userSystemAttributeSubscription, this.dataStructureSubscription]);
  };

  private tracker = async () => {
    const { user, setAttribute } = this.props;
    if (this.userSystemAttributeSubscription?.ready()) {
      const attrQuery = QueryBuilder.select().from(CollectionNames.USER_SYSTEM_ATTRIBUTES).where(eq('id', user.username)).build();
      const attr = (await this.repository.find<UserSystemAttributes>(attrQuery)) ?? ValueFactory.initializeUserAttribute();
      setAttribute(attr);
      this.prepareColumns(attr);
    }
  };

  private stopTracker = () => {
    this.userSystemAttributeSubscription?.stop();
    this.dataStructureSubscription?.stop();
    this.computation?.stop();
  };

  public componentWillUnmount() {
    this.stopTracker();
    this.stopActionLogTracker();
  }

  public async componentDidUpdate(prevProps: Readonly<Props>) {
    const { update, systemAttributes, selectedAlarmDomain } = this.props;
    if (
      this.props.transactions !== prevProps.transactions ||
      update != prevProps.update ||
      this.isActionHistoryVisible() != this.actionHistoryVisible
    ) {
      this.refreshActionHistory();
    }
    const { currentPage, rows } = this.props;
    const shouldUpdate = update != undefined && update != prevProps.update;
    const shouldUpdateColumns = selectedAlarmDomain?.dataSource !== prevProps.selectedAlarmDomain?.dataSource;

    if (shouldUpdate || shouldUpdateColumns) {
      if (currentPage != undefined) {
        this.setState({ currentPage });
      }
      if (rows != undefined) {
        this.setState({ rows });
      }
      await this.prepareColumns(systemAttributes);
    }
  }

  private getColumns = () => {
    const {
      case: { columns },
    } = this.props;
    return columns[this.getKey()] ?? [];
  };

  private calculateUrl = async (record: TransactionData) => {
    const { selectedAlarmDomain } = this.props;

    if (selectedAlarmDomain && selectedAlarmDomain.dataSource === record.name) {
      const urlParams = new URLSearchParams();
      urlParams.set('index', record.id);
      urlParams.set('domain', selectedAlarmDomain.name);
      urlParams.set('transaction', selectedAlarmDomain.dataSource);
      urlParams.set('keyField', selectedAlarmDomain.keyField);
      urlParams.set('keyValue', record.variables[selectedAlarmDomain.keyField]);
      urlParams.forEach((value, key) => {
        urlParams.set(key, value.replace(/ /g, '%20'));
      });
      return `/cases/${selectedAlarmDomain.name}/detail/${urlParams.toString()}`;
    }

    const settings = await this.repository.find<CaseSettings>(
      QueryBuilder.select().from(CollectionNames.CASE_SETTINGS).where(eq('dataSource', record.name)).build(),
    );
    if (settings == undefined) return;
    const urlParams = new URLSearchParams();
    urlParams.set('index', record.id);
    urlParams.set('domain', settings.name);
    urlParams.set('transaction', settings.dataSource);
    urlParams.set('keyField', settings.keyField);
    urlParams.set('keyValue', record.variables[settings.keyField]);
    urlParams.forEach((value, key) => {
      urlParams.set(key, value.replace(/ /g, '%20'));
    });
    return `/cases/${settings.name}/detail/${urlParams.toString()}`;
  };

  private onRowClick = async (e: DataTableRowClickEventParams) => {
    const {
      openInNewPage,
      router: { navigate, params },
      selectedAlarmDomain,
    } = this.props;
    const record: TransactionData = e.data;
    const url = await this.calculateUrl(record);

    let settings = selectedAlarmDomain;
    if (!settings || settings.dataSource !== record.name) {
      settings = await this.repository.find<CaseSettings>(
        QueryBuilder.select().from(CollectionNames.CASE_SETTINGS).where(eq('dataSource', record.name)).build(),
      );
    }

    if (settings == undefined) return;
    if (url == undefined) return;
    if (openInNewPage) {
      window.open(`/${NavigationUtil.getTenant()}${url}`, '_blank', 'noopener noreferrer');
    } else {
      if (!params.domain) {
        navigate(`/cases/${settings.name}`);
      }
      navigate(url);
    }
  };

  private prepareColumns = async (attribute: UserSystemAttributes) => {
    const { selectedAlarmDomain, user } = this.props;
    const riskType = user.activeRiskType;
    const columns: CustomColumnProps[] = [
      {
        field: 'id.keyword',
        sortField: 'id.keyword',
        header: LanguageUtil.get({ id: 'pages.case.table.id', defaultMessage: 'ID' }),
        alignHeader: 'left',
        className: 'min-w-[220px] 2xl:min-w-[250px]',
        copyable: true,
        filterable: true,
        addFilter: (e: TransactionData) => {
          this.addFilter('id', e.id, 'ID', DataType.STRING);
        },
        onCopy: (e: TransactionData) => {
          return e.id;
        },
        body: (record: TransactionData) => {
          return (
            <div
              className={`flex w-full text-xs 2xl:text-md items-start text-f-black-3 justify-start relative group`}
              data-testid={`${ComponentName.CASE}_id_${record.id}`}
            >
              <CustomTableContentWrapper clipSize={20} text={record.id} />
            </div>
          );
        },
      },
      {
        field: 'score',
        header: LanguageUtil.get({ id: 'pages.case.table.score', defaultMessage: 'Score' }),
        sortField: `result.${riskType}.score`,
        alignHeader: 'left',
        columnKey: 'score',
        copyable: true,
        filterable: true,
        addFilter: (e: TransactionData) => {
          this.addFilter(`result.${riskType}.score`, e.result[riskType]?.score, 'Score', DataType.NUMBER);
        },
        onCopy: (e: TransactionData) => {
          return e.result[riskType].score.toString();
        },
        body: (record: TransactionData) => {
          const result = record.result[riskType];
          if (result == undefined) return;
          return (
            <div
              className={`flex flex-col text-xs 2xl:text-md justify-center items-center ${this.getStatusItem(result.resolution)} w-[40px] rounded-full  h-[22px]`}
              data-testid={`${ComponentName.CASE}_score_${record.id}`}
            >
              {result.score}
            </div>
          );
        },
      },
      {
        field: 'rule',
        header: LanguageUtil.get({ id: 'pages.case.table.rule', defaultMessage: 'Rule' }),
        sortable: false,
        alignHeader: 'left',
        columnKey: 'ruleCount',
        className: 'min-w-[150px] 2xl:min-w-[170px]',
        body: (record: TransactionData, { rowIndex }) => {
          const result = record.result[user.activeRiskType];
          if (result == undefined) return;
          return (
            <div
              className={'flex items-start text-[#728EFF] text-xs 2xl:text-md justify-start relative group'}
              data-testid={`${ComponentName.CASE}_rule_container_${record.id}`}
            >
              {result.rules?.length > 0 ? (
                <Tooltip target={`#rule_${rowIndex}`}>
                  <div className='grid grid-cols-3 gap-x-3 gap-y-1.5'>
                    {result.rules?.map((r, i) => (
                      <span key={r.name} data-testid={`${ComponentName.CASE}_rule_${i}_${record.id}`}>
                        {r.name} - {r.score} - {r.resolution}
                      </span>
                    ))}
                  </div>
                </Tooltip>
              ) : null}
              <span id={`rule_${rowIndex}`} data-testid={`${ComponentName.CASE}_rule_title_${record.id}`} className={'detection'}>
                {LanguageUtil.get({ id: 'pages.case.table.detectedBy' }, { 0: `${result.rules?.length.toString() || 0}` })}
              </span>
            </div>
          );
        },
      },
      {
        field: 'detection',
        header: LanguageUtil.get({ id: 'pages.case.table.detection', defaultMessage: 'Detection' }),
        sortable: false,
        alignHeader: 'left',
        columnKey: 'detection',
        className: 'min-w-[180px] 2xl:min-w-[200px]',
        copyable: true,
        onCopy: (record: TransactionData) => {
          const result = record.result[user.activeRiskType];
          return result?.rules?.map((rule) => rule.name).join(', ') || '';
        },
        body: (record: TransactionData, { rowIndex }) => {
          const result = record.result[user.activeRiskType];
          if (result == undefined) return;
          const detectionText = result.rules?.map((rule) => rule.name).join(', ') || '-';
          return (
            <div
              className={'flex items-start text-[#728EFF] text-xs 2xl:text-md justify-start relative group'}
              data-testid={`${ComponentName.CASE}_detection_container_${record.id}`}
            >
              {result.rules?.length > 0 ? (
                <Tooltip target={`#detection_${rowIndex}`}>
                  <div className='grid grid-cols-3 gap-x-3 gap-y-1.5'>
                    {result.rules?.map((r, i) => (
                      <span key={r.name} data-testid={`${ComponentName.CASE}_detection_${i}_${record.id}`}>
                        {r.name} - {r.score} - {r.resolution}
                      </span>
                    ))}
                  </div>
                </Tooltip>
              ) : null}
              <span id={`detection_${rowIndex}`} data-testid={`${ComponentName.CASE}_detection_title_${record.id}`} className={'detection'}>
                <CustomTableContentWrapper clipSize={30} text={detectionText} />
              </span>
            </div>
          );
        },
      },
      {
        field: `result.${riskType}.metadata.assignee.id.keyword`,
        sortField: `result.${riskType}.metadata.assignee.id.keyword`,
        header: LanguageUtil.get({ id: 'pages.case.table.assignee', defaultMessage: 'Assignee' }),
        alignHeader: 'left',
        columnKey: `result.${riskType}.metadata.assignee.id.keyword`,
        className: 'min-w-[300px] 2xl:min-w-[350px]',
        copyable: true,
        filterable: true,
        addFilter: (e: TransactionData) => {
          this.addFilter(
            `result.${riskType}.metadata.assignee.id.keyword`,
            e.result[user.activeRiskType].metadata?.assignee?.id || 'System',
            'Assignee',
            DataType.STRING,
            true,
          );
        },
        onCopy: (e: TransactionData) => {
          return e.result[user.activeRiskType].metadata?.assignee?.id || 'No one assigned';
        },
        body: (record: TransactionData) => {
          const result = record.result[user.activeRiskType];
          if (result == undefined) return;
          let value;
          const assignee = result.metadata?.assignee?.id;
          const assigneeFirstName = result.metadata?.assignee?.firstName;
          const assigneeLastName = result.metadata?.assignee?.lastName;
          if (assigneeFirstName != undefined && assigneeFirstName != '' && assigneeLastName != undefined && assigneeLastName != '') {
            value = `${assigneeFirstName} ${assigneeLastName}`;
          } else {
            value = assignee || LanguageUtil.get({ id: 'pages.case.table.noOneAssigned', defaultMessage: 'No one assigned' });
          }
          return (
            <div className={'flex items-center text-f-black-3 text-xs 2xl:text-md'}>
              {value.length > 0 && (
                <div className={`w-7 h-7 flex items-center justify-center rounded-full bg-[#F4F3FF] mr-1`}>
                  {`${assigneeFirstName?.substring(0, 1)?.toUpperCase() || ''}${assigneeLastName?.substring(0, 1)?.toUpperCase() || ''}` || 'S'}
                </div>
              )}
              <span>
                <CustomTableContentWrapper clipSize={35} text={value} data-testid={`${ComponentName.CASE}_assign_${record.id}`} />
              </span>
            </div>
          );
        },
      },
      {
        field: `result.${riskType}.resolution.keyword`,
        sortField: `result.${riskType}.resolution.keyword`,
        header: LanguageUtil.get({ id: 'pages.case.table.resolution', defaultMessage: 'Resolution' }),
        alignHeader: 'left',
        columnKey: `result.${riskType}.resolution.keyword`,
        className: 'min-w-[150px] 2xl:min-w-[170px]',
        copyable: true,
        filterable: true,
        addFilter: (e: TransactionData) => {
          this.addFilter(`result.${riskType}.resolution.keyword`, e.result[user.activeRiskType].resolution, 'Resolution', DataType.STRING, true);
        },
        onCopy: (e: TransactionData) => {
          return CaseEventTextUtil.getStatusText(e.result[user.activeRiskType].resolution);
        },
        body: (record: TransactionData) => {
          const result = record.result[user.activeRiskType];
          if (result == undefined) return;
          return (
            <div
              className={`flex flex-col justify-center items-center text-xs 2xl:text-md rounded-full ${this.getStatusItem(result.resolution)} min-w-[86px] max-w-[150px] h-[22px]`}
              data-testid={`${ComponentName.CASE}_resolution_${record.id}`}
            >
              {CaseEventTextUtil.getStatusText(result.resolution)}
            </div>
          );
        },
      },
      {
        field: `result.${riskType}.status.keyword`,
        sortField: `result.${riskType}.status.keyword`,
        header: LanguageUtil.get({ id: 'pages.case.table.status', defaultMessage: 'Status' }),
        alignHeader: 'left',
        columnKey: `result.${riskType}.status.keyword`,
        className: 'min-w-[250px]',
        copyable: true,
        filterable: true,
        addFilter: (e: TransactionData) => {
          this.addFilter(`result.${riskType}.status.keyword`, e.result[user.activeRiskType].status, 'Status', DataType.STRING, true);
        },
        onCopy: (e: TransactionData) => {
          return CaseEventTextUtil.getStatusText(e.result[user.activeRiskType].status);
        },
        body: (record: TransactionData) => {
          const result = record.result[user.activeRiskType];
          if (result == undefined) return;
          return (
            <div
              className={`flex flex-col justify-center items-center text-xs 2xl:text-md rounded-full ${this.getStatusItem(result.status)} w-[170px] h-[22px]`}
              data-testid={`${ComponentName.CASE}_status_${record.id}`}
            >
              {CaseEventTextUtil.getStatusText(result.status)}
            </div>
          );
        },
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({ id: 'pages.case.table.createdAt', defaultMessage: 'Created At' }),
        alignHeader: 'left',
        columnKey: 'createdAt',
        className: 'min-w-[270px] 2xl:min-w-[300px]',
        copyable: true,
        filterable: true,
        addFilter: (e: TransactionData) => {
          this.addFilter(`createdAt`, e.createdAt, 'Date', DataType.DATE);
        },
        onCopy: (e: TransactionData) => {
          return DateFormatUtil.formatEpochForDisplay(e.createdAt);
        },
        body: (record: TransactionData) => {
          return (
            <div className={'flex items-start text-f-black-3 text-xs 2xl:text-md'}>
              <CustomTableContentWrapper
                text={DateFormatUtil.formatEpochForDisplay(record.createdAt)}
                tooltipText={DateFormatUtil.formatForTooltip(record.createdAt, 'epochMillis')}
                data-testid={`${ComponentName.CASE}_createdAt_${record.id}`}
              />
            </div>
          );
        },
      },
      {
        field: `result.${riskType}.metadata.review.endDate`,
        header: LanguageUtil.get({ id: 'pages.case.table.closedAt', defaultMessage: 'Closed At' }),
        alignHeader: 'left',
        columnKey: `result.${riskType}.metadata.review.endDate`,
        className: 'min-w-[270px] 2xl:min-w-[300px]',
        copyable: true,
        filterable: true,
        sortField: `result.${riskType}.metadata.review.endDate`,
        addFilter: (e: TransactionData) => {
          this.addFilter(`result.${riskType}.metadata.review.endDate`, e.result?.[riskType]?.metadata?.review?.endDate, 'Closed At', DataType.DATE);
        },
        onCopy: (e: TransactionData) => {
          const endDate = e.result?.[riskType]?.metadata?.review?.endDate;
          return endDate ? DateFormatUtil.formatEpochForDisplay(endDate) : '';
        },
        body: (record: TransactionData) => {
          const endDate = record.result?.[riskType]?.metadata?.review?.endDate;
          return (
            <div className={'flex items-start text-f-black-3 text-xs 2xl:text-md'}>
              <CustomTableContentWrapper
                text={endDate ? DateFormatUtil.formatEpochForDisplay(endDate) : ''}
                tooltipText={endDate ? DateFormatUtil.formatForTooltip(endDate, 'epochMillis') : undefined}
                data-testid={`${ComponentName.CASE}_closedAt_${record.id}`}
              />
            </div>
          );
        },
      },
      {
        field: 'firstActionAt',
        header: LanguageUtil.get({ id: 'pages.case.table.firstActionAt', defaultMessage: 'First Action At' }),
        sortable: false,
        alignHeader: 'left',
        columnKey: 'firstActionAt',
        className: 'min-w-[270px] 2xl:min-w-[300px]',
        copyable: true,
        onCopy: (e: TransactionData) => {
          const firstActionAt = this.state.actionHistory[e.id]?.firstActionAt;
          return firstActionAt ? DateFormatUtil.formatEpochForDisplay(firstActionAt) : '';
        },
        body: (record: TransactionData) => {
          const history = this.state.actionHistory[record.id];
          const firstAction = history?.actions.find((a) => a.timestamp == history.firstActionAt);
          return (
            <div className={'flex items-start text-f-black-3 text-xs 2xl:text-md'}>
              <CustomTableContentWrapper
                text={history?.firstActionAt ? DateFormatUtil.formatEpochForDisplay(history.firstActionAt) : ''}
                tooltipText={firstAction ? this.formatAction(firstAction) : undefined}
                data-testid={`${ComponentName.CASE}_firstActionAt_${record.id}`}
              />
            </div>
          );
        },
      },
      {
        field: 'actionHistory',
        header: LanguageUtil.get({ id: 'pages.case.table.actionHistory', defaultMessage: 'Action History' }),
        sortable: false,
        alignHeader: 'center',
        columnKey: 'actionHistory',
        className: 'min-w-[150px]',
        body: (record: TransactionData) => {
          const actions = this.state.actionHistory[record.id]?.actions ?? [];
          return (
            <div className={'flex flex-col justify-center items-center min-w-[86px] max-w-[150px] text-xs 2xl:text-md'}>
              {actions.length > 0 && (
                <button
                  onClick={() => this.showActionHistory(actions)}
                  className={'text-[#458EF7]'}
                  data-testid={`${ComponentName.CASE}_actionHistory_${record.id}`}
                >
                  {LanguageUtil.get({ id: 'pages.case.table.showActionHistory', defaultMessage: 'Show History' })}
                </button>
              )}
            </div>
          );
        },
      },
    ];
    if (selectedAlarmDomain != undefined && selectedAlarmDomain.keyField != undefined) {
      const transactionQuery = QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('id', selectedAlarmDomain.dataSource)).build();
      const transaction = await this.repository.find<TransactionJsonModel>(transactionQuery);
      const keyFieldHeader = transaction?.variables?.find((v) => v?.name == selectedAlarmDomain.keyField)?.description || selectedAlarmDomain.keyField;
      columns.splice(1, 0, {
        field: `variables.${selectedAlarmDomain.keyField}.keyword`,
        sortField: `variables.${selectedAlarmDomain.keyField}.keyword`,
        header: keyFieldHeader,
        alignHeader: 'left',
        columnKey: `variables.${selectedAlarmDomain.keyField}.keyword`,
        className: 'min-w-[200px] 2xl:min-w-[220px]',
        copyable: true,
        filterable: true,
        addFilter: (e: TransactionData) => {
          this.addFilter(
            `variables.${selectedAlarmDomain.keyField}.keyword`,
            e.variables[selectedAlarmDomain.keyField],
            selectedAlarmDomain.keyField,
            DataType.STRING,
          );
        },
        onCopy: (record: TransactionData) => {
          return record.variables[selectedAlarmDomain.keyField];
        },
        body: (record: TransactionData) => {
          const value = record.variables[selectedAlarmDomain.keyField];
          const testId = `${ComponentName.CASE}_variable_${selectedAlarmDomain.keyField}_${record.id}`;
          return (
            <div className={`flex items-start text-f-black-3 text-xs 2xl:text-md`} data-testid={testId}>
              {this.renderStringCell(value, testId)}
            </div>
          );
        },
      });
      const closedAtIndex = columns.findIndex((c) => c.field === `result.${riskType}.metadata.review.endDate`);
      const insertIndex = closedAtIndex !== -1 ? closedAtIndex + 1 : columns.length;
      columns.splice(
        insertIndex,
        0,
        {
          field: `result.${riskType}.metadata.code.keyword`,
          sortField: `result.${riskType}.metadata.code.keyword`,
          header: LanguageUtil.get({ id: 'pages.case.table.reason', defaultMessage: 'Reason' }),
          alignHeader: 'left',
          columnKey: `result.${riskType}.metadata.code.keyword`,
          className: 'min-w-[220px] 2xl:min-w-[240px]',
          addFilter: (record: TransactionData) => {
            this.addFilter(`result.${riskType}.metadata.code.keyword`, record.result[user.activeRiskType]?.metadata?.code, 'Reason', DataType.STRING);
          },
          filterable: true,
          copyable: true,
          onCopy: (record: TransactionData) => {
            const result = record.result[user.activeRiskType];
            if (result == undefined) return;
            return result.metadata?.code ? selectedAlarmDomain.closeCodes.find((reason) => reason.code == result.metadata?.code)?.code || 'N/A' : 'N/A';
          },
          body: (record: TransactionData) => {
            const result = record.result[user.activeRiskType];
            return (
              <div
                className={`flex flex-col justify-center items-start text-xs 2xl:text-md rounded-[4px]  h-[22px]`}
                data-testid={`${ComponentName.CASE}_reason_${record.id}`}
              >
                <CustomTableContentWrapper
                  clipSize={20}
                  text={result?.metadata?.code || 'N/A'}
                  // text='AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA'
                  // text='aaaaaaaaaaaaaa'
                  data-testid={`${ComponentName.CASE}_reason_${record.id}`}
                />
              </div>
            );
          },
        },
        {
          field: 'timeToFA',
          header: LanguageUtil.get({ id: 'pages.case.table.timeToFa', defaultMessage: 'Time to FA' }),
          sortable: false,
          alignHeader: 'center',
          columnKey: 'timeToFA',
          className: 'min-w-[150px]',
          body: (record: TransactionData) => {
            const result = record.result[user.activeRiskType];
            if (result == undefined) return;
            const timeToFA = this.calculateTimeToFA(record, result);
            const hasFirstAssignment = this.hasFirstAssignment(result);
            const formattedTime = this.formatSlaRemainingTime(timeToFA);
            return (
              <div
                className={`${timeToFA < 0 ? 'bg-[#FFF1F3]' : hasFirstAssignment && timeToFA > 0 ? 'bg-[#DDFBEA]' : 'bg-[#F2F4F7]'} h-6 border border-1 rounded-full px-4 flex items-center justify-center gap-x-2`}
              >
                <CustomTableContentWrapper text={formattedTime} data-testid={`${ComponentName.CASE}_tfa_${record.id}`} />
                <Icon icon={'first-seen'} size={18} />
              </div>
            );
          },
        },
        {
          field: 'timeToDone',
          header: LanguageUtil.get({ id: 'pages.case.table.timeToDone', defaultMessage: 'Time to Done' }),
          sortable: false,
          alignHeader: 'center',
          columnKey: 'timeToDone',
          className: 'min-w-[150px]',
          body: (record: TransactionData) => {
            const result = record.result[user.activeRiskType];
            if (result == undefined) return;
            const timeToDone = this.calculateTimeToDone(record, result);
            const isClosedForSla = this.getFrozenClosedAt(result) != undefined;
            const formattedTime = this.formatSlaRemainingTime(timeToDone);
            return (
              <div
                className={`${timeToDone < 0 ? 'bg-[#FFF1F3]' : isClosedForSla && timeToDone > 0 ? 'bg-[#DDFBEA]' : 'bg-[#F2F4F7]'}  h-6 border border-1 rounded-full px-4 flex items-center justify-center gap-x-2`}
              >
                <CustomTableContentWrapper text={formattedTime} data-testid={`${ComponentName.CASE}_ttd_${record.id}`} />
                <Icon icon={'schedule'} size={14} />
              </div>
            );
          },
        },
      );
    }
    const transactionFields = await this.prepareTransactionFields();
    columns.push(...transactionFields);
    columns.push({
      field: 'response',
      header: LanguageUtil.get({ id: 'pages.case.table.response', defaultMessage: 'Response' }),
      sortable: false,
      alignHeader: 'center',
      columnKey: 'response',
      className: 'min-w-[150px]',
      body: (record: TransactionData) => {
        return (
          <div className={'flex flex-col justify-center items-center min-w-[86px] max-w-[150px] text-xs 2xl:text-md'}>
            <button
              onClick={() => this.showResult(record.result[user.activeRiskType])}
              className={'text-[#458EF7]'}
              data-testid={`${ComponentName.CASE}_response_${record.id}`}
            >
              {LanguageUtil.get({ id: 'pages.case.table.showResponse', defaultMessage: 'Show Response' })}
            </button>
          </div>
        );
      },
    });
    columns.push({
      field: 'failures',
      header: LanguageUtil.get({ id: 'pages.case.table.failures', defaultMessage: 'Failures' }),
      sortable: false,
      alignHeader: 'center',
      columnKey: 'failures',
      className: 'min-w-[150px]',
      body: (record: TransactionData) => {
        return (
          <div className={'flex flex-col justify-center items-center min-w-[86px] max-w-[150px] text-f-black-3 text-xs 2xl:text-md'}>
            {record.result.failures.length == 0 ? (
              'N/A'
            ) : (
              <button onClick={() => this.showFailures(record.result.failures)} data-testid={`${ComponentName.CASE}_failures_${record.id}`}>
                Show
                {LanguageUtil.get({ id: 'pages.case.table.failures', defaultMessage: 'Failures' })}
              </button>
            )}
          </div>
        );
      },
    });
    const table = attribute.table.find((t) => t.component == ComponentName.CASE && t.id == selectedAlarmDomain?.dataSource);
    if (attribute && table) {
      for (const hiddenColumn of table.hiddenColumns) {
        const column = columns.find((c) => c.field == hiddenColumn);
        if (column) {
          column.hidden = true;
        }
      }
    }
    if (table && table.columnOrder.length > 0) {
      table.columnOrder.forEach((column, index) => {
        const col = columns.findIndex((c) => c.field == column);
        if (col != -1) {
          const column = columns.splice(col, 1)[0];
          columns.splice(index, 0, column);
        }
      });
    }
    this.props.changeListDataLoading(false);
    const key = this.getKey();
    this.props.case.columns[key] = columns;
    this.props.changeCaseColumns(this.props.case.columns);
  };

  private getKey() {
    const { filterKey, selectedAlarmDomain } = this.props;
    return filterKey ?? selectedAlarmDomain?.dataSource ?? 'default';
  }

  private readonly doneStatuses: TransactionReason[] = [
    TransactionReason.NOT_FRAUD,
    TransactionReason.FRAUD,
    TransactionReason.COMPLIANT,
    TransactionReason.IN_COMPLIANT,
  ];

  private isCaseDone(result: TransactionResult): boolean {
    return this.doneStatuses.includes(result.status);
  }

  private isApiClosed(result: TransactionResult): boolean {
    if (!this.isCaseDone(result)) {
      return false;
    }
    if (result.metadata?.closeType === 'API') {
      return true;
    }
    return this.isSystemClosed(result);
  }

  private isSystemClosed(result: TransactionResult): boolean {
    if (!this.isCaseDone(result)) {
      return false;
    }
    const assigneeId = result.metadata?.assignee?.id;
    return assigneeId == undefined || assigneeId === '' || assigneeId === 'System';
  }

  private hasFirstAssignment(result: TransactionResult): boolean {
    return result.metadata?.assignee?.assignmentDuration != undefined;
  }

  private getFrozenClosedAt(result: TransactionResult): number | undefined {
    const review = result.metadata?.review;
    if (review == undefined) {
      return undefined;
    }
    if (review.endDate == undefined || review.endDate == null) {
      return undefined;
    }
    return review.endDate;
  }

  private calculateSlaFromClosedAt(slaMilliseconds: number, createdAt: number, closedAt: number | undefined): number {
    if (closedAt == undefined) {
      return slaMilliseconds;
    }
    return slaMilliseconds - (closedAt - createdAt);
  }

  private calculateTimeToFA(record: TransactionData, result: TransactionResult): number {
    const { selectedAlarmDomain } = this.props;
    const slaMinutes = selectedAlarmDomain?.sla.timeToFirstAssign.value;
    if (!slaMinutes) {
      return 0;
    }

    const slaMilliseconds = slaMinutes * 60 * 1000;
    const createdAt = record.createdAt;
    const assignmentDuration = result.metadata?.assignee?.assignmentDuration;
    if (assignmentDuration != undefined) {
      return slaMilliseconds - assignmentDuration;
    }

    if (result.status === TransactionReason.WAITING_FOR_REVIEW) {
      return slaMilliseconds - (moment().valueOf() - createdAt);
    }

    return slaMilliseconds;
  }

  private calculateTimeToDone(record: TransactionData, result: TransactionResult): number {
    const { selectedAlarmDomain } = this.props;
    const slaMinutes = selectedAlarmDomain?.sla.timeToDone.value;
    if (!slaMinutes) {
      return 0;
    }

    const slaMilliseconds = slaMinutes * 60 * 1000;
    const createdAt = record.createdAt;
    const frozenClosedAt = this.getFrozenClosedAt(result);

    if (this.isApiClosed(result)) {
      return this.calculateSlaFromClosedAt(slaMilliseconds, createdAt, frozenClosedAt);
    }

    if (frozenClosedAt != undefined) {
      return slaMilliseconds - (frozenClosedAt - createdAt);
    }

    if (!this.isCaseDone(result)) {
      return slaMilliseconds - (moment().valueOf() - createdAt);
    }

    return slaMilliseconds - (record.updatedAt - createdAt);
  }

  private formatSlaRemainingTime(milliseconds: number): string {
    const sign = Math.sign(milliseconds);
    if (sign == -1 || sign == -0) {
      return Math.ceil(milliseconds / (60 * 60 * 1000)) + 'h ' + Math.ceil((milliseconds % (60 * 60 * 1000)) / (60 * 1000)) + 'm';
    }
    return Math.floor(milliseconds / (60 * 60 * 1000)) + 'h ' + Math.floor((milliseconds % (60 * 60 * 1000)) / (60 * 1000)) + 'm';
  }

  private TransactionFieldsStyle = (dataType: DataType) => {
    switch (dataType) {
      case DataType.STRING:
        return 'min-w-[220px] 2xl:min-w-[240px]';
      case DataType.DATE:
        return 'min-w-[200px] 2xl:min-w-[220px]';
      case DataType.NUMBER:
        return 'min-w-[160px] 2xl:min-w-[180px]';
      case DataType.BOOLEAN:
        return 'min-w-[80px] 2xl:min-w-[100px]';
    }
  };

  private prepareTransactionFields = async (): Promise<ColumnProps[]> => {
    const { selectedAlarmDomain } = this.props;
    if (selectedAlarmDomain == undefined) return [];
    const transactionQuery = QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('id', selectedAlarmDomain.dataSource)).build();
    const transaction = await this.repository.find<TransactionJsonModel>(transactionQuery);
    if (transaction == undefined) return [];
    return transaction.variables
      .filter((v) => v.name != selectedAlarmDomain.keyField)
      .map((v) => ({
        field: `variables.${v.description && v.description.length > 0 ? v.description : v.name}`,
        sortField: `variables.${v.name}${v.properties.dataType == DataType.STRING ? '.keyword' : ''}`,
        header: v.description && v.description.length > 0 ? v.description : v.name,
        alignHeader: 'left',
        columnKey: `variables.${v.name}`,
        className: this.TransactionFieldsStyle(v.properties.dataType) ?? 'min-w-[170px]',
        copyable: true,
        filterable: v.properties.dataType != 'Array',
        addFilter:
          v.properties.dataType == DataType.STRUCTURE
            ? undefined
            : (record: TransactionData) => {
                const value = record.variables?.[v.name];
                const field = v.properties.dataType == DataType.STRING ? `variables.${v.name}.keyword` : `variables.${v.name}`;
                this.addFilter(field, value, v.description && v.description.length > 0 ? v.description : v.name, v.properties.dataType);
              },
        onCopy: (record: TransactionData) => {
          const value = record.variables?.[v.name];
          if (value == undefined) return 'N/A';
          switch (v.properties.dataType) {
            case DataType.STRING:
              return value;
            case DataType.NUMBER:
              return typeof value == 'number' ? ValueUtil.formatNumber(value) : value;
            case DataType.DATE:
              return DateFormatUtil.formatForTable(value, (v.properties as DateVariableProperties).format);
            case DataType.BOOLEAN:
              return value ? 'true' : 'false';
            case DataType.OBJECT:
            case DataType.STRUCTURE:
            case DataType.ARRAY:
              return JSON.stringify(value);
            default:
              return value;
          }
        },
        body: (record: TransactionData) => {
          return (
            <div data-testid={`${ComponentName.CASE}_variable_${v.name}_${record.id}`} className={'text-xs 2xl:text-md'}>
              {this.getTransactionFieldBody(record, v)}            </div>
          );
        },
      }));
  };

  private getFilter(props?: Props | Readonly<Props>) {
    const { filterKey, page } = props ?? this.props;
    if (!filterKey) return page.filter;
    return page.filters[filterKey] ?? page.filter;
  }

  private addFilter = async (field: string, value: any, label: string, dataType: DataType, categorical: boolean = false) => {
    const { changePageFilter, onChangeFilter } = this.props;
    const filter: { filter: FilterType; items: CustomFilterFields[] } = this.getFilter();
    if (categorical) {
      filter.filter.fields.push({
        field: field,
        label: label,
        enabled: true,
        search: {
          type: 'CATEGORICAL',
          value: {
            values: [value],
          } as CustomFilterCategorical,
        },
      });
    } else {
      switch (dataType) {
        case DataType.STRING:
        case DataType.NUMBER:
          filter.filter.fields.push({
            field: field,
            label: label,
            enabled: true,
            search: {
              type: 'LITERAL',
              value: {
                properties: {
                  dataType: dataType,
                },
                operator: 'eq',
                value: value,
              } as CustomFilterLiteralSearch,
            },
          });
          break;
        case DataType.DATE:
          filter.filter.fields.push({
            field: field,
            label: label,
            enabled: true,
            search: {
              type: 'DATE',
              value: {
                startDate: {
                  label: DateFormatUtil.formatEpochForDisplay(value),
                  value: value,
                  type: 'Absolute',
                },
                endDate: {
                  label: DateFormatUtil.formatEpochForDisplay(value),
                  value: value,
                  type: 'Absolute',
                },
              } as CustomFilterDateSearch,
            },
          });
          break;
        case DataType.BOOLEAN:
          await this.addFilter(field, value, label, dataType, true);
          break;
        default:
          return;
      }
    }
    changePageFilter(filter);
    onChangeFilter(this.parseFilter());
  };

  private parseFilter = () => {
    const filter = this.getFilter().filter;
    return FilterUtil.parse(filter);
  };

  private renderStringCell = (value: string, dataTestId?: string): ReactNode => {
    if (UrlUtil.isUrl(value)) {
      return (
        <a href={UrlUtil.toHref(value)} target={'_blank'} rel={'noopener noreferrer'} onClick={(e) => e.stopPropagation()}>
          <CustomTableContentWrapper text={value} clipSize={20} asLink data-testid={dataTestId} />
        </a>
      );
    }
    return <CustomTableContentWrapper text={value} clipSize={20} data-testid={dataTestId} />;
  };

  private getTransactionFieldBody = (record: TransactionData, variable: TransactionVariableJsonModel): any => {
    if (record.variables == undefined) return;
    const value = record.variables[variable.name];
    if (value == undefined) return 'N/A';
    if (UrlUtil.isUrl(value)) return this.renderStringCell(value);
    switch (variable.properties.dataType) {
      case DataType.STRING:
        if (value.length == 0) return 'N/A';
        return <CustomTableContentWrapper text={value} clipSize={20} />;
      case DataType.NUMBER: {
        if (typeof value == 'number') {
          const formatted = ValueUtil.formatNumber(value);
          return <CustomTableContentWrapper text={formatted} clipSize={15} showEllipsis={true} />;
        }
        if (typeof value == 'string') {
          try {
            const parsed = Number(value);
            if (isNaN(parsed)) {
              return;
            }
            const formatted = ValueUtil.formatNumber(parsed);
            return <CustomTableContentWrapper text={formatted} clipSize={15} showEllipsis={true} />;
          } catch (e) {
            return <CustomTableContentWrapper text={value} clipSize={15} />;
          }
        }
        break;
      }
      case DataType.DATE:
        return (
          <CustomTableContentWrapper
            text={DateFormatUtil.formatForTable(value, (variable.properties as DateVariableProperties).format)}
            tooltipText={DateFormatUtil.formatForTooltip(value, (variable.properties as DateVariableProperties).format)}
            clipSize={50}
          />
        );
      case DataType.BOOLEAN:
        return value || value == 'true' ? 'true' : 'false';
      case DataType.OBJECT:
      case DataType.STRUCTURE:
      case DataType.ARRAY:
        return (
          <a
            className={'text-[#458EF7] text-xs 2xl:text-md'}
            onClick={async () => {
              const struct = await this.getStructByName(variable.properties);
              this.setState({ structureVisible: true, structureData: this.getStructureData(value, struct) });
            }}
          >
            Items
          </a>
        );
      default:
        return value;
    }
  };

  private structureModalClose = () => {
    const { structureVisible } = this.state;
    this.setState({ structureVisible: !structureVisible });
  };

  private waitForDataStructures = (): Promise<void> => {
    const subscription = this.dataStructureSubscription;
    if (!subscription || subscription.ready()) return Promise.resolve();
    return new Promise<void>((resolve) => {
      const computation = subscribe.Tracker.autorun(() => {
        if (subscription.ready()) {
          resolve();
          Promise.resolve().then(() => computation.stop());
        }
      }, [subscription]);
    });
  };

  private getStructByName = async (properties: any): Promise<DataStructure | undefined> => {
    if (properties.dataType !== DataType.STRUCTURE || !properties.name) {
      return undefined;
    }
    const fetchPromise = this.waitForDataStructures().then(() => {
      const structQuery = QueryBuilder.select().from(CollectionNames.DATA_STRUCTURES).where(eq('id', properties.name)).build();
      return this.repository.find<DataStructure>(structQuery);
    });
    return fetchPromise;
  };

  private getStructureData = (data: any, struct?: DataStructure): TreeNode[] => {
    const variables = (struct?.properties as ObjectVariableProperties | undefined)?.variables;
    let result: TreeNode[] = [];
    for (const key in data) {
      const type = variables?.find((v) => v.name === key)?.properties?.dataType;
      result = result.concat(this.convertItem(data[key], key, type));
    }
    return result;
  };

  private convertItem(item: any, key: any, type?: DataType): TreeNode[] {
    let newItem: TreeNode = {
      key: generateUUID(),
      label: key,
      children: [],
    };
    if (Array.isArray(item)) {
      item.forEach((subItem, index) => {
        const subKey = `${index}`;
        newItem.children = newItem?.children?.concat(this.convertItem(subItem, subKey, type));
      });
    } else if (typeof item === 'object' && item !== null) {
      for (const subKey in item) {
        newItem.children = newItem?.children?.concat(this.convertItem(item[subKey], subKey));
      }
    } else {
      const value = typeof item === 'number' ? type === DataType.DATE ? DateFormatUtil.formatEpochForDisplay(item) : ValueUtil.formatNumber(item) : type === DataType.DATE ? DateFormatUtil.formatForTable(item) : item;
      newItem.label = `${key}: ${value}`;
    }
    return [newItem];
  }

  private getStatusItem = (status: TransactionReason | TransactionResultReason) => {
    switch (status) {
      case TransactionReason.FRAUD:
      case TransactionReason.IN_COMPLIANT:
      case TransactionResultReason.DECLINED:
        return 'bg-[#FBE5E4] text-[#C34135]';
      case TransactionReason.NOT_FRAUD:
      case TransactionReason.COMPLIANT:
      case TransactionResultReason.APPROVED:
        return 'bg-[#E8FBF1] text-[#027A48]';
      case TransactionReason.IN_REVIEW:
        return 'bg-[#DFEAFD] text-[#175CD3]';
      case TransactionReason.WAITING_FOR_REVIEW:
        return 'bg-[#F6F6F9] text-[#4F4F4F]';
      case TransactionResultReason.ESCALATED:
        return 'bg-[#FFEEDD] text-[#FF7020]';
      case TransactionReason.ON_HOLD:
        return 'bg-[#FFB55D] text-white';
    }
  };

  private onPageChange = (event: CustomPaginatorProps) => {
    const { onPageChange } = this.props;
    this.setState({ currentPage: event.page, rows: event.rows });
    onPageChange(event.page, event.rows);
  };

  private formatAction = (action: CaseActionHistoryItem): string => {
    return `${CaseEventTextUtil.getDetail(action)} ${DateFormatUtil.formatEpochForDisplay(action.timestamp)} (${action.username})`;
  };

  private onRowClassName = (record: TransactionData) => {
    const { selectedTransactionId } = this.props;
    const isSelectedId = selectedTransactionId != undefined && selectedTransactionId == record.id;
    const hasError = record.result.failures.length > 0;
    if (isSelectedId) {
      return '!bg-[#F5F5F5] 2xl:!h-[52px] 2xl:!max-h-[52px] !h-[44px] !max-h-[44px] ';
    }
    return `2xl:!h-[52px] 2xl:!max-h-[52px] !h-[44px] !max-h-[44px]  ${hasError ? '!bg-[#FFF7F7]' : 'bg-white'}`;
  };

  private onSelectionChange = (e: any) => {
    dispatch(setSelectedItems(e.value));
  };

  private showFailures = (failures: any[]) => {
    this.setState({ transactionFailures: failures, transactionFailureVisible: true });
  };

  private closeFailures = () => {
    this.setState({ transactionFailures: [], transactionFailureVisible: false });
  };

  private showResult = (result: TransactionResult) => {
    this.setState({ transactionResult: result, transactionResultVisible: true });
  };

  private showActionHistory = (actions: CaseActionHistoryItem[]) => {
    this.setState({ selectedActionHistory: actions, actionHistoryVisible: true });
  };

  private closeActionHistory = () => {
    this.setState({ actionHistoryVisible: false });
  };

  private closeResult = () => {
    this.setState({ transactionResult: undefined, transactionResultVisible: false });
  };

  private onColReorder = (e: DataTableColReorderParams) => {
    const { systemAttributes, selectedAlarmDomain, setAttribute, case: caseState, changeCaseColumns } = this.props;
    if (!Array.isArray(e.columns)) return;
    const columns: ColumnProps[] = (e.columns as any).map((c: any) => c.props);
    const order = columns
      .filter((c) => c.field != undefined)
      .map((c) => c.field)
      .filter((c) => c != undefined) as string[];

    const key = this.getKey();
    const currentColumns = caseState.columns[key] ?? [];
    const columnByField = new Map(currentColumns.map((column) => [column.field, column]));
    const reorderedColumns = order
      .map((field) => columnByField.get(field))
      .filter((column): column is CustomColumnProps => column != undefined);
    const remainingColumns = currentColumns.filter((column) => column.field != undefined && !order.includes(column.field));
    changeCaseColumns({
      ...caseState.columns,
      [key]: [...reorderedColumns, ...remainingColumns],
    });

    const table = systemAttributes.table.find((t) => t.component == ComponentName.CASE && t.id == selectedAlarmDomain?.dataSource);
    if (table) {
      table.columnOrder = order;
    } else {
      systemAttributes.table.push({
        component: ComponentName.CASE,
        id: selectedAlarmDomain?.dataSource,
        columnOrder: order,
        hiddenColumns: [],
        pageSize: 10,
      });
    }
    setAttribute(systemAttributes);
  };

  private getOrder = (): DataTableSortMeta[] => {
    const { sort } = this.props;
    return sort.map((s) => {
      return {
        field: s.field,
        order: s.operator == 'ASC' ? 1 : -1,
      } as DataTableSortMeta;
    });
  };
}

interface Props extends FormicaProps, ReduxProps {
  transactions: TransactionData[];
  selectedAlarmDomain: CaseSettings | undefined;
  paginationFixed: boolean;
  onPageChange: (page: number, rows: number) => void;
  onSortChange?: (sort: Order[]) => void;
  total: number;
  rows: number;
  currentPage: number;
  openInNewPage?: boolean;
  selectedTransactionId?: string;
  loading?: boolean;
  update?: number;
  multiple?: boolean;
  sort: Order[];
  onChangeFilter: (filter: Filter) => void;
  filterKey?: string;
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user as User,
  systemAttributes: state.auth.attributes as UserSystemAttributes,
  component: state.component as ComponentState,
  case: state.case as CaseState,
  page: state.page as PageState,
});

const connector = connect(mapStateToProps, { changeCaseColumns, setAttribute, changeListDataLoading, changePageFilter });
type ReduxProps = ConnectedProps<typeof connector>;

class State {
  rows = 10;
  currentPage = 1;
  transactionFailures: any[] = [];
  transactionFailureVisible: boolean = false;
  transactionResult: TransactionResult | undefined;
  transactionResultVisible: boolean = false;
  structureVisible: boolean = false;
  structureData: TreeNode[] = [];
  actionHistory: Record<string, CaseActionHistory> = {};
  selectedActionHistory: CaseActionHistoryItem[] = [];
  actionHistoryVisible: boolean = false;
}

export default withFormica(connector(CaseTransactionsActivityComponent));
